import { useQuery } from '@tanstack/react-query';

import { RutterItem } from '~hooks/useRutter/models';
import useRutter from '~hooks/useRutter/useRutter';
import { useStores } from '~store';
import { Nullable } from '~types/Nullable';
import fetchAllRutterPages from '~utils/rutter/fetchAllRutterPages';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { RARELY_CHANGED_DATA_STALE_TIME_IN_MS } from './constants';

interface RutterItemsQueryData {
  connectionId: Nullable<string>;
  items: RutterItem[];
}

interface UseRutterItemsQueryProps {
  enabled: boolean;
}

export default function useRutterItemsQuery({ enabled }: UseRutterItemsQueryProps) {
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const { getItems, getSynchronizedConnection } = useRutter();

  return useQuery<RutterItemsQueryData>({
    queryKey: ['rutterItems', companyId],
    enabled,
    // a connection that is not found is not cached, so a failed lookup is retried on the next use
    staleTime: (query) =>
      query.state.data?.connectionId ? RARELY_CHANGED_DATA_STALE_TIME_IN_MS : 0,
    retry: false,
    queryFn: async ({ signal }) => {
      const synchronizedConnection = await getSynchronizedConnection(signal);

      if (!synchronizedConnection) {
        return { connectionId: null, items: [] };
      }

      const items = await fetchAllRutterPages(
        (pagination) => getItems(synchronizedConnection.id, pagination, signal),
        { signal },
      );

      return { connectionId: synchronizedConnection.id, items };
    },
  });
}
